import CloseIcon from 'mdi-react/CloseIcon'
import FileUploadOutlineIcon from 'mdi-react/FileUploadOutlineIcon'
import React, { useCallback } from 'react'
import { makeStyles } from 'tss-react/mui'

import { IconButton, TextField, TextFieldProps, Theme } from '@mui/material'

function getFileFromEvent(event: React.ChangeEvent<HTMLInputElement>) {
  return event.currentTarget.files && event.currentTarget.files.length > 0 ? event.currentTarget.files[0] : undefined
}

export function isFile(fileOrString: string | File | undefined) {
  return !!fileOrString && typeof fileOrString !== 'string'
}

export type FileInputProps = Omit<TextFieldProps, 'onChange' | 'value' | 'variant'> & {
  onChange: (value: string | File) => any
  value: string | File
}

function useFileInputHandlers(onChange: FileInputProps['onChange']) {
  const inputRef = React.useRef<HTMLInputElement>(null)
  const handleDelete = useCallback(() => {
    onChange('')
  }, [onChange])
  const triggerUpload = useCallback(() => {
    if (inputRef.current) {
      inputRef.current.click()
    }
  }, [])
  const handleUpload = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = getFileFromEvent(e)
      if (file) {
        onChange(file)
      }
    },
    [onChange]
  )
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target
      onChange(value)
    },
    [onChange]
  )
  return {
    handleDelete,
    handleChange,
    handleUpload,
    triggerUpload,
    inputRef,
  }
}

export const FileInput = React.memo(
  ({ className, style, disabled, value, onChange, ...otherProps }: FileInputProps) => {
    const { classes, cx } = useStyles()
    const { handleChange, handleDelete, handleUpload, triggerUpload, inputRef } = useFileInputHandlers(onChange)
    return (
      <div className={cx(classes.fileInput, className)} style={style}>
        <TextField
          type="text"
          className={classes.textInput}
          value={isFile(value) ? (value as File).name : (value as string)}
          onChange={!isFile(value) ? handleChange : undefined}
          disabled={disabled}
          fullWidth
          InputLabelProps={{ shrink: !!value }}
          InputProps={{
            endAdornment: (
              <div className={classes.inputControls}>
                {!!value && (
                  <IconButton className={classes.actionIcon} size="small" color="primary" onClick={handleDelete}>
                    <CloseIcon />
                  </IconButton>
                )}
                <IconButton className={classes.actionIcon} size="small" color="primary" onClick={triggerUpload}>
                  <FileUploadOutlineIcon />
                </IconButton>
              </div>
            ),
          }}
          {...otherProps}
        />

        <input ref={inputRef} className={classes.hiddenInput} onChange={handleUpload} type="file" disabled={disabled} />
      </div>
    )
  }
)

FileInput.displayName = 'FileInput'

const useStyles = makeStyles()(({ spacing }: Theme) => ({
  fileInput: {
    position: 'relative',
    display: 'flex',
  },
  textInput: {},
  inputControls: {
    height: spacing(4),
    display: 'flex',
    alignItems: 'center',
  },
  hiddenInput: {
    position: 'absolute',
    right: 0,
    bottom: 0,
    opacity: 0,
    width: 0,
    height: 0,
  },
  actionIcon: {
    marginLeft: spacing(),
  },
  helperText: {},
}))
